import { ReactNode } from 'react';
import { motion } from 'framer-motion';
import { Card } from '../../atoms/Card/Card';
import { AnimatedNumber } from '../../atoms/AnimatedNumber/AnimatedNumber';
import { ENERGY_THRESHOLDS } from '../../../constants';
import './GaugeCard.css';

// Utility functions colocated with usage
function calculatePercentage(value: number, max: number): number {
  return Math.round((value / max) * 100);
}

function getThresholdClass(percentage: number): string {
  if (percentage >= ENERGY_THRESHOLDS.danger) return 'GaugeCard-value--danger';
  if (percentage >= ENERGY_THRESHOLDS.warning) return 'GaugeCard-value--warning';
  return '';
}

interface GaugeCardProps {
  title: string;
  value: number;
  unit?: string;
  maxValue?: number;
  minValue?: number;
  showPercentage?: boolean;
  showBar?: boolean;
  secondaryStat?: { value: React.ReactNode; label: string }; // Optional secondary stat
  children?: ReactNode; // For context tooltip
}

export function GaugeCard({
  title,
  value,
  unit = 'MW',
  maxValue,
  minValue = 0,
  showPercentage = false,
  showBar = true,
  secondaryStat,
  children,
}: GaugeCardProps) {
  const percentage = maxValue ? calculatePercentage(value, maxValue) : 0;
  const thresholdClass = maxValue ? getThresholdClass(percentage) : '';

  return (
    <Card className="GaugeCard">
      <div className="GaugeCard-header">
        <h3>{title}</h3>
      </div>

      <div className={`GaugeCard-value ${thresholdClass}`}>
        <AnimatedNumber value={value} decimals={0} className="GaugeCard-valueText" />
        <span className="GaugeCard-unit">{unit}</span>
        {secondaryStat && (
          <span className="GaugeCard-secondary">
            <span className="GaugeCard-secondaryValue">{secondaryStat.value}</span>
            {secondaryStat.label && <span className="GaugeCard-secondaryLabel">{secondaryStat.label}</span>}
          </span>
        )}
      </div>

      {showBar && maxValue && (
        <>
          <div className="GaugeCard-bar">
            <motion.div
              className="GaugeCard-fill"
              initial={{ width: `${percentage}%` }}
              animate={{ width: `${percentage}%` }}
              transition={{ duration: 1, ease: "easeInOut" }}
            />
          </div>
          <div className="GaugeCard-range">
            {minValue} - {maxValue} {unit}
          </div>
        </>
      )}

      {children}
    </Card>
  );
}
